import axios, { AxiosError, type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';

const baseURL = import.meta.env.VITE_API_URL?.trim() || '';
const PREFIX = '/api/supplier';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 20_000,
});

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t) {
    cfg.headers = cfg.headers ?? {};
    cfg.headers['Authorization'] = `Bearer ${t}`;
  }
  return cfg;
});

// Единая очередь refresh: при пачке 401 рефрешим один раз, остальные ждут.
let refreshInflight: Promise<string | null> | null = null;
const pendingQueue: Array<(token: string | null) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  const refresh = useAuthStore.getState().refreshToken;
  if (!refresh) return null;
  try {
    const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
      `${baseURL}${PREFIX}/auth/refresh`,
      { refresh_token: refresh },
      { timeout: 10_000 },
    );
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError<{ error?: string; code?: string }>) => {
    const status = err.response?.status;
    const original = err.config as (typeof err.config & { _retry?: boolean }) | undefined;
    const msg = err.response?.data?.error ?? err.message ?? 'Ошибка запроса';

    if (status === 401 && original && !original._retry) {
      const url = original.url ?? '';
      if (url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/register')) {
        useAuthStore.getState().logout();
        return Promise.reject(err);
      }

      original._retry = true;

      if (refreshInflight) {
        return new Promise((resolve, reject) => {
          pendingQueue.push((token) => {
            if (!token) return reject(err);
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            resolve(api(original));
          });
        });
      }

      refreshInflight = refreshAccessToken();
      const newToken = await refreshInflight;
      refreshInflight = null;

      const queue = pendingQueue.splice(0);
      for (const cb of queue) cb(newToken);

      if (!newToken) {
        useAuthStore.getState().logout();
        toast.error('Сессия истекла, войдите ещё раз');
        return Promise.reject(err);
      }
      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
      return api(original);
    }

    if (status === 401) {
      useAuthStore.getState().logout();
      toast.error('Сессия истекла, войдите ещё раз');
    } else if (status && status >= 400 && status < 500) {
      toast.error(msg);
    } else if (status && status >= 500) {
      toast.error(`Ошибка сервера: ${msg}`);
    }
    return Promise.reject(err);
  },
);

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await api.get<T>(`${PREFIX}${url}`, { params });
  return data;
}

export async function post<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${url}`, body);
  return data;
}

export async function patch<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await api.patch<T>(`${PREFIX}${url}`, body);
  return data;
}

export async function del<T>(url: string): Promise<T> {
  const { data } = await api.delete<T>(`${PREFIX}${url}`);
  return data;
}

export async function postForm<T>(url: string, form: FormData): Promise<T> {
  const { data } = await api.post<T>(`${PREFIX}${url}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}
